/* eslint-disable react/prop-types */
/**
 * Pass J1 — manifest drift badge.
 *
 * Renders a steel-gray pill "Manifest changed mid-run" only when
 * the campaign detail's ``manifest_drift.manifest_drift_detected``
 * is true. Tooltip names the pre/post sha256.
 *
 * Pass L1 — co-located ``<CampaignInFlightPill>`` surfacing the
 * Pass K3 ``is_in_flight: bool`` payload. Steel-gray sibling pill
 * "Campaign in progress · counts provisional" rendered only when
 * ``drift.is_in_flight === true``. Tooltip names the in-flight
 * provisional-counts contract. Co-located in this file per the
 * Pass L brief: "do NOT introduce a new component just for this;
 * either co-locate it next to the drift badge or reuse the same
 * wrapper".
 *
 * Defence palette only. UK spelling. Tabular numerals on any
 * embedded numeric (current copy has none — the pill is static
 * text — but tabular-nums is kept on the style for forward
 * compatibility if the operator adds a per-pod shard count later).
 */
import { DEFENCE_PALETTE } from "@/components/phase19/placementUtils";


function _basePillStyle() {
    return {
        background: DEFENCE_PALETTE.steelGray,
        color: "#FFFFFF",
        fontVariantNumeric: "tabular-nums",
    };
}


const _PILL_CLASS = (
    "inline-flex items-center rounded-full px-2 py-0.5 "
    + "text-[10px] font-medium uppercase tracking-wide leading-none"
);


export default function ManifestDriftBadge({ drift }) {
    if (!drift || !drift.manifest_drift_detected) return null;
    const first = drift.manifest_sha256_first_seen || "—";
    const last = drift.manifest_sha256_last_seen || "—";
    const tooltip = (
        "FDM registry was rebuilt while this campaign was running. "
        + `First sample saw ${first}; latest saw ${last}. `
        + "Procurement reviewers should inspect the rebuild diff for "
        + "any aero-calibration state changes between the two points."
    );
    return (
        <span
            className={_PILL_CLASS}
            style={_basePillStyle()}
            data-testid="manifest-drift-badge"
            title={tooltip}
        >
            Manifest changed mid-run · {drift.manifest_sha256_distinct_count}
        </span>
    );
}


export function CampaignInFlightPill({ drift }) {
    // Pass L1 — render only on explicit ``true``. ``null``,
    // ``undefined``, ``false`` all fall through to no-op so a
    // completed campaign view stays uncluttered.
    if (!drift || drift.is_in_flight !== true) return null;
    const tooltip = (
        "Calibration coverage and fallback counts are aggregated "
        + "from in-flight per-pod shards. Final counts will land "
        + "once the campaign completes and shards are reduced."
    );
    // Pass M1.3 — surface ``pods_reporting`` count when > 0 so the
    // operator can see how many pods are currently writing into
    // the in-flight shard set. Defence-palette unchanged.
    // Tabular-numeric formatting applies to the count.
    const pods = Number(drift.pods_reporting || 0);
    return (
        <span
            className={_PILL_CLASS + " ml-2"}
            style={_basePillStyle()}
            data-testid="campaign-in-flight-pill"
            title={tooltip}
        >
            {pods > 0 ? (
                <>
                    Campaign in progress ·{" "}
                    <span
                        data-testid="campaign-in-flight-pill-pods"
                        style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                        {pods}
                    </span>
                    {" pods reporting · counts provisional"}
                </>
            ) : (
                "Campaign in progress · counts provisional"
            )}
        </span>
    );
}

